import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { RobotoFont } from './fonts/roboto.js';
import './App.css';

const API = 'https://api.idekod.com.tr';

/* ---------- yardımcılar ---------- */

const bugun = () => new Date().toISOString().slice(0, 10);
const ayBasi = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const trTarih = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
};

/* saniye -> "7s 45dk" */
const sureMetni = (sn) => {
  const n = Number(sn);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const saat = Math.floor(n / 3600);
  const dk = Math.round((n % 3600) / 60);
  if (!saat) return `${dk} dk`;
  return dk ? `${saat} sa ${dk} dk` : `${saat} sa`;
};

/* saniye -> 7.75 (Excel'de toplanabilsin diye ondalık saat) */
const ondalikSaat = (sn) => {
  const n = Number(sn);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round((n / 3600) * 100) / 100;
};

const DURUM_ETIKET = { 0: 'Onay Bekliyor', 1: 'Aktif', 2: 'Pasif', 3: 'Bekleyen' };
const ROL_ETIKET = { 0: 'Kullanıcı', 1: 'Admin', 2: 'Ekip Şefi' };
const ONAY_ETIKET = { 0: 'Bekliyor', 1: 'Onaylandı', 3: 'İptal Edildi' };
const ONAY_ROZET = { 0: 'badge--warning', 1: 'badge--success', 3: 'badge--danger' };

/* ---------- rapor tanımları ----------
   Her rapor: kolonlar (başlık + değer üreticisi) tek yerde tanımlanır;
   tablo, Excel ve PDF aynı tanımı kullanır.                              */

const RAPORLAR = {
  personel: {
    baslik: 'Personel Raporu',
    aciklama: 'Kayıtlı personel listesi, durum ve hakediş bilgileri',
    dosya: 'personel-raporu',
    tarihAraligi: false,
    kolonlar: [
      { baslik: 'Ad Soyad', al: (r) => r.RNAME },
      { baslik: 'Kullanıcı Adı', al: (r) => r.USERN },
      { baslik: 'Telefon', al: (r) => r.telno },
      { baslik: 'TCKN', al: (r) => r.TCKN },
      { baslik: 'E-posta', al: (r) => r.eposta },
      { baslik: 'Şehir', al: (r) => r.SEHIR },
      { baslik: 'İşe Başlangıç', al: (r) => trTarih(r.girist) },
      { baslik: 'Kurum', al: (r) => r.COMPANY },
      { baslik: 'Durum', al: (r) => DURUM_ETIKET[Number(r.STATE)] ?? r.STATE },
      { baslik: 'Rol', al: (r) => ROL_ETIKET[Number(r.role)] ?? r.role },
      { baslik: 'Hakediş', al: (r) => r.hakedis ?? 0, sayi: true },
    ],
  },

  izin: {
    baslik: 'İzin Raporu',
    aciklama: 'Seçilen tarih aralığıyla kesişen izin talepleri',
    dosya: 'izin-raporu',
    tarihAraligi: true,
    kolonlar: [
      { baslik: 'Ad Soyad', al: (r) => r.RNAME },
      { baslik: 'Telefon', al: (r) => r.TELNO },
      { baslik: 'İzin Türü', al: (r) => r.IZINTUR },
      { baslik: 'Başlangıç', al: (r) => trTarih(r.BASTAR) },
      { baslik: 'Bitiş', al: (r) => trTarih(r.BITTAR) },
      { baslik: 'Saat', al: (r) => (r.BASSAAT ? `${r.BASSAAT} - ${r.BITSAAT || ''}` : '-') },
      { baslik: 'Düşülen Gün', al: (r) => Number(r.DUSULECEK) || 0, sayi: true },
      { baslik: 'Durum', al: (r) => ONAY_ETIKET[Number(r.ONAY)] ?? r.ONAY },
      { baslik: 'Açıklama', al: (r) => r.ACIKLAMA || '' },
      { baslik: 'İptal Nedeni', al: (r) => r.IPTAL_ACK || '' },
    ],
  },

  mesaiDetay: {
    baslik: 'Giriş / Çıkış Raporu',
    aciklama: 'Gün gün giriş-çıkış saatleri ve çalışma süreleri',
    dosya: 'giris-cikis-raporu',
    tarihAraligi: true,
    kolonlar: [
      { baslik: 'Ad Soyad', al: (r) => r.RNAME },
      { baslik: 'Telefon', al: (r) => r.telno },
      { baslik: 'Tarih', al: (r) => trTarih(r.tarih) },
      { baslik: 'Giriş', al: (r) => r.giris || '-' },
      { baslik: 'Çıkış', al: (r) => r.cikis || '-' },
      { baslik: 'Çalışma', al: (r) => sureMetni(r.calisma_sn) },
      { baslik: 'Saat (ondalık)', al: (r) => ondalikSaat(r.calisma_sn), sayi: true },
    ],
  },

  mesaiOzet: {
    baslik: 'Çalışma Süresi Özeti',
    aciklama: 'Personel bazında toplam çalışma süresi',
    dosya: 'calisma-suresi-ozeti',
    tarihAraligi: true,
    kolonlar: [
      { baslik: 'Ad Soyad', al: (r) => r.RNAME },
      { baslik: 'Telefon', al: (r) => r.telno },
      { baslik: 'Çalışılan Gün', al: (r) => Number(r.gun_sayisi) || 0, sayi: true },
      { baslik: 'Toplam Çalışma', al: (r) => sureMetni(r.toplam_sn) },
      { baslik: 'Toplam Saat (ondalık)', al: (r) => ondalikSaat(r.toplam_sn), sayi: true },
      { baslik: 'Eksik Kayıt', al: (r) => Number(r.eksik_kayit) || 0, sayi: true },
    ],
  },
};

const SEKMELER = [
  { key: 'personel', etiket: 'Personel' },
  { key: 'izin', etiket: 'İzinler' },
  { key: 'mesaiDetay', etiket: 'Giriş / Çıkış' },
  { key: 'mesaiOzet', etiket: 'Çalışma Süresi' },
];

const Raporlar = () => {
  const role = localStorage.getItem('role');
  const username = localStorage.getItem('username');

  const [sekme, setSekme] = useState('personel');
  const [bas, setBas] = useState(ayBasi());
  const [bit, setBit] = useState(bugun());
  const [telno, setTelno] = useState('');
  const [durum, setDurum] = useState('');
  const [onay, setOnay] = useState('');
  const [izintur, setIzintur] = useState('');
  const [q, setQ] = useState('');

  const [personeller, setPersoneller] = useState([]);
  const [rows, setRows] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hata, setHata] = useState('');

  const tanim = RAPORLAR[sekme];

  useEffect(() => {
    fetch(`${API}/api/personel?role=${role}&username=${username}`)
      .then((r) => r.json())
      .then((d) => setPersoneller(Array.isArray(d) ? d : []))
      .catch(() => setPersoneller([]));
  }, [role, username]);

  const raporGetir = useCallback(async () => {
    setLoading(true);
    setHata('');

    const p = new URLSearchParams({ role: role || '', username: username || '' });
    let url;

    if (sekme === 'personel') {
      if (durum !== '') p.set('durum', durum);
      if (q) p.set('q', q);
      url = `${API}/api/rapor/personel?${p}`;
    } else if (sekme === 'izin') {
      p.set('bas', bas); p.set('bit', bit);
      if (telno) p.set('telno', telno);
      if (izintur) p.set('izintur', izintur);
      if (onay !== '') p.set('onay', onay);
      url = `${API}/api/rapor/izin?${p}`;
    } else {
      p.set('bas', bas); p.set('bit', bit);
      p.set('mod', sekme === 'mesaiOzet' ? 'ozet' : 'detay');
      if (telno) p.set('telno', telno);
      url = `${API}/api/rapor/mesai?${p}`;
    }

    try {
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || 'Rapor alınamadı.');
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setOzet(data.ozet || null);
    } catch (err) {
      console.error('Rapor hatası:', err);
      setHata(err.message);
      setRows([]);
      setOzet(null);
    } finally {
      setLoading(false);
    }
  }, [sekme, bas, bit, telno, durum, onay, izintur, q, role, username]);

  useEffect(() => { raporGetir(); }, [raporGetir]);

  /* ---------- dışa aktarma ---------- */

  const matris = () => rows.map((r) => tanim.kolonlar.map((k) => k.al(r)));

  const excelAktar = () => {
    const veri = rows.map((r) => {
      const satir = {};
      tanim.kolonlar.forEach((k) => { satir[k.baslik] = k.al(r); });
      return satir;
    });

    const ws = XLSX.utils.json_to_sheet(veri, { header: tanim.kolonlar.map((k) => k.baslik) });
    ws['!cols'] = tanim.kolonlar.map((k) => ({ wch: Math.max(12, k.baslik.length + 4) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapor');
    XLSX.writeFile(wb, `${tanim.dosya}-${bugun()}.xlsx`);
  };

  const pdfAktar = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.addFileToVFS('Roboto-Regular.ttf', RobotoFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');

    doc.setFontSize(14);
    doc.text(tanim.baslik, 14, 14);
    doc.setFontSize(9);
    const altBaslik = tanim.tarihAraligi
      ? `${trTarih(bas)} - ${trTarih(bit)}  |  ${rows.length} kayıt`
      : `${rows.length} kayıt`;
    doc.text(altBaslik, 14, 20);

    doc.autoTable({
      head: [tanim.kolonlar.map((k) => k.baslik)],
      body: matris().map((satir) => satir.map((h) => (h === null || h === undefined ? '' : String(h)))),
      startY: 25,
      styles: { font: 'Roboto', fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [61, 115, 127], textColor: 255 },
    });

    doc.save(`${tanim.dosya}-${bugun()}.pdf`);
  };

  /* ---------- özet kutuları ---------- */

  const ozetKutulari = () => {
    if (!ozet) return null;
    if (sekme === 'personel') {
      return [
        ['Toplam Personel', ozet.toplam],
        ['Aktif', ozet.aktif],
        ['Pasif', ozet.pasif],
        ['Toplam Hakediş', ozet.toplamHakedis],
      ];
    }
    if (sekme === 'izin') {
      return [
        ['Toplam Talep', ozet.toplam],
        ['Onaylanan', ozet.onayli],
        ['Bekleyen', ozet.bekleyen],
        ['Düşülen Gün', ozet.toplamGun],
      ];
    }
    return [
      ['Kayıt', ozet.kayit],
      ['Personel', ozet.personel],
      ['Toplam Çalışma', sureMetni(ozet.toplamSn)],
      ['Eksik Kayıt', ozet.eksikKayit],
    ];
  };

  const hucreIcerik = (kolon, satir) => {
    const deger = kolon.al(satir);
    if (sekme === 'izin' && kolon.baslik === 'Durum') {
      return <span className={`badge ${ONAY_ROZET[Number(satir.ONAY)] || ''}`}>{deger}</span>;
    }
    if (sekme === 'personel' && kolon.baslik === 'Durum') {
      const aktif = Number(satir.STATE) === 1;
      return <span className={`badge ${aktif ? 'badge--success' : ''}`}>{deger}</span>;
    }
    return deger === null || deger === undefined || deger === '' ? '-' : deger;
  };

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Raporlar</h1>
          <p className="page__subtitle">Personel, izin ve mesai kayıtlarını filtreleyip dışa aktarın</p>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {SEKMELER.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={sekme === s.key}
            className={`tabs__item ${sekme === s.key ? 'tabs__item--active' : ''}`}
            onClick={() => setSekme(s.key)}
          >
            {s.etiket}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card__header">
          <h2 className="card__title">{tanim.baslik}</h2>
          <p className="card__subtitle">{tanim.aciklama}</p>
        </div>

        <div className="card__body">
          <div className="form-grid form-grid--three">
            {tanim.tarihAraligi && (
              <>
                <div className="form-field">
                  <label className="form-label">Başlangıç Tarihi</label>
                  <input type="date" value={bas} onChange={(e) => setBas(e.target.value)} className="form-control" />
                </div>
                <div className="form-field">
                  <label className="form-label">Bitiş Tarihi</label>
                  <input type="date" value={bit} onChange={(e) => setBit(e.target.value)} className="form-control" />
                </div>
              </>
            )}

            {sekme !== 'personel' && (
              <div className="form-field">
                <label className="form-label">Personel</label>
                <select value={telno} onChange={(e) => setTelno(e.target.value)} className="form-control">
                  <option value="">Tümü</option>
                  {personeller.map((p) => (
                    <option key={p.TELNO} value={p.TELNO}>{p.RNAME}</option>
                  ))}
                </select>
              </div>
            )}

            {sekme === 'personel' && (
              <>
                <div className="form-field">
                  <label className="form-label">Durum</label>
                  <select value={durum} onChange={(e) => setDurum(e.target.value)} className="form-control">
                    <option value="">Tümü</option>
                    <option value="1">Aktif</option>
                    <option value="2">Pasif</option>
                    <option value="0">Onay Bekliyor</option>
                    <option value="3">Bekleyen</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Ara</label>
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ad, telefon, kullanıcı adı, e-posta"
                    className="form-control"
                  />
                </div>
              </>
            )}

            {sekme === 'izin' && (
              <>
                <div className="form-field">
                  <label className="form-label">İzin Türü</label>
                  <select value={izintur} onChange={(e) => setIzintur(e.target.value)} className="form-control">
                    <option value="">Tümü</option>
                    <option value="Yıllık İzin">Yıllık İzin</option>
                    <option value="Günlük İzin">Günlük İzin</option>
                    <option value="Saatlik İzin">Saatlik İzin</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Onay Durumu</label>
                  <select value={onay} onChange={(e) => setOnay(e.target.value)} className="form-control">
                    <option value="">Tümü</option>
                    <option value="0">Bekliyor</option>
                    <option value="1">Onaylandı</option>
                    <option value="3">İptal Edildi</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="btn-group u-mt-4">
            <button type="button" onClick={raporGetir} className="btn btn--primary" disabled={loading}>
              {loading ? 'Hazırlanıyor…' : 'Raporu Yenile'}
            </button>
            <button type="button" onClick={excelAktar} className="btn btn--ghost" disabled={!rows.length}>
              Excel'e Aktar
            </button>
            <button type="button" onClick={pdfAktar} className="btn btn--ghost" disabled={!rows.length}>
              PDF'e Aktar
            </button>
          </div>
        </div>

        {ozet && !loading && !hata && (
          <div className="card__body">
            <div className="stat-grid">
              {ozetKutulari().map(([etiket, deger]) => (
                <div className="stat-tile" key={etiket}>
                  <div className="stat-tile__label">{etiket}</div>
                  <div className="stat-tile__value">{deger}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="state-block">
            <span className="spinner" aria-hidden="true" />
            <span>Rapor hazırlanıyor…</span>
          </div>
        )}

        {!loading && hata && (
          <div className="state-block state-block--error">
            <span>Rapor alınamadı.</span>
            <span className="form-helper">{hata}</span>
          </div>
        )}

        {!loading && !hata && (
          <div className="table-wrapper">
            <table className="data-table data-table--striped">
              <thead>
                <tr>
                  {tanim.kolonlar.map((k) => <th key={k.baslik}>{k.baslik}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.PK ?? `${r.telno}-${i}`}>
                    {tanim.kolonlar.map((k) => (
                      <td key={k.baslik} data-label={k.baslik}>{hucreIcerik(k, r)}</td>
                    ))}
                  </tr>
                ))}
                {!rows.length && (
                  <tr className="empty-row">
                    <td colSpan={tanim.kolonlar.length}>
                      Seçilen kriterlere uyan kayıt bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Raporlar;
